'use client';

import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["slots", "slotProps", "className", "classes", "sx", "children"];
import * as React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { ComposerAttachmentList, useComposerContext } from '@mui/x-chat-headless';
import { styled, createUseThemeProps } from "../internals/zero-styled/index.mjs";
import { useChatComposerUtilityClasses } from "./chatComposerClasses.mjs";
import DefaultCloseIcon from "../icons/DefaultCloseIcon.mjs";
import DefaultFileIcon from "../icons/DefaultFileIcon.mjs";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const useThemeProps = createUseThemeProps('MuiChatComposerAttachmentList');
const ChatComposerAttachmentListStyled = styled('div', {
  name: 'MuiChatComposer',
  slot: 'AttachmentList',
  overridesResolver: (_, styles) => styles.attachmentList
})(({
  theme
}) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(0.5),
  padding: theme.spacing(0.5, 0)
}));
const AttachmentChip = styled('div', {
  name: 'MuiChatComposer',
  slot: 'AttachmentChip',
  skipVariantsResolver: true
})(({
  theme
}) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  maxWidth: 180,
  padding: theme.spacing(0.25, 0.75),
  border: '1px solid',
  borderColor: (theme.vars || theme).palette.divider,
  borderRadius: theme.shape.borderRadius * 2,
  backgroundColor: (theme.vars || theme).palette.background.paper,
  fontSize: theme.typography.caption.fontSize,
  lineHeight: theme.typography.caption.lineHeight,
  color: (theme.vars || theme).palette.text.secondary
}));
const AttachmentThumbnail = styled('img', {
  name: 'MuiChatComposer',
  slot: 'AttachmentThumbnail',
  skipVariantsResolver: true
})({
  width: 20,
  height: 20,
  objectFit: 'cover',
  borderRadius: 3,
  flexShrink: 0
});
const AttachmentFileName = styled('span', {
  name: 'MuiChatComposer',
  slot: 'AttachmentFileName',
  skipVariantsResolver: true
})({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  minWidth: 0
});
const AttachmentRemoveButton = styled('button', {
  name: 'MuiChatComposer',
  slot: 'AttachmentRemoveButton',
  skipVariantsResolver: true
})(({
  theme
}) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 16,
  height: 16,
  padding: 0,
  border: 'none',
  borderRadius: '50%',
  backgroundColor: 'transparent',
  color: (theme.vars || theme).palette.text.disabled,
  cursor: 'pointer',
  flexShrink: 0,
  fontSize: 12,
  '&:hover': {
    color: (theme.vars || theme).palette.text.primary,
    backgroundColor: (theme.vars || theme).palette.action.hover
  },
  '& svg': {
    width: '1em',
    height: '1em'
  }
}));
const AttachmentFileIconWrapper = styled('span', {
  name: 'MuiChatComposer',
  slot: 'AttachmentFileIcon',
  skipVariantsResolver: true
})(({
  theme
}) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 20,
  height: 20,
  flexShrink: 0,
  fontSize: 16,
  color: (theme.vars || theme).palette.text.disabled,
  '& svg': {
    width: '1em',
    height: '1em'
  }
}));
function DefaultAttachmentListContent() {
  const composer = useComposerContext();
  return /*#__PURE__*/_jsx(React.Fragment, {
    children: composer.attachments.map(attachment => /*#__PURE__*/_jsxs(AttachmentChip, {
      children: [attachment.previewUrl ? /*#__PURE__*/_jsx(AttachmentThumbnail, {
        src: attachment.previewUrl,
        alt: attachment.file.name
      }) : /*#__PURE__*/_jsx(AttachmentFileIconWrapper, {
        children: /*#__PURE__*/_jsx(DefaultFileIcon, {})
      }), /*#__PURE__*/_jsx(AttachmentFileName, {
        children: attachment.file.name
      }), /*#__PURE__*/_jsx(AttachmentRemoveButton, {
        type: "button",
        "aria-label": `Remove ${attachment.file.name}`,
        onClick: () => composer.removeAttachment(attachment.localId),
        children: /*#__PURE__*/_jsx(DefaultCloseIcon, {})
      })]
    }, attachment.localId))
  });
}
const ChatComposerAttachmentList = /*#__PURE__*/React.forwardRef(function ChatComposerAttachmentList(inProps, ref) {
  const props = useThemeProps({
    props: inProps,
    name: 'MuiChatComposerAttachmentList'
  });
  const {
      slots,
      slotProps,
      className,
      classes: classesProp,
      sx,
      children
    } = props,
    other = _objectWithoutPropertiesLoose(props, _excluded);
  const classes = useChatComposerUtilityClasses(classesProp);
  return /*#__PURE__*/_jsx(ComposerAttachmentList, _extends({
    ref: ref
  }, other, {
    slots: _extends({
      attachmentList: slots?.attachmentList ?? ChatComposerAttachmentListStyled
    }, slots),
    slotProps: _extends({}, slotProps, {
      attachmentList: _extends({
        className: clsx(classes.attachmentList, className),
        sx
      }, slotProps?.attachmentList)
    }),
    children: children ?? /*#__PURE__*/_jsx(DefaultAttachmentListContent, {})
  }));
});
if (process.env.NODE_ENV !== "production") ChatComposerAttachmentList.displayName = "ChatComposerAttachmentList";
process.env.NODE_ENV !== "production" ? ChatComposerAttachmentList.propTypes = {
  // ----------------------------- Warning --------------------------------
  // | These PropTypes are generated from the TypeScript type definitions |
  // | To update them edit the TypeScript types and run "pnpm proptypes"  |
  // ----------------------------------------------------------------------
  classes: PropTypes.object,
  className: PropTypes.string,
  slotProps: PropTypes.object,
  slots: PropTypes.object,
  sx: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.bool])), PropTypes.func, PropTypes.object])
} : void 0;
export { ChatComposerAttachmentList };